/**
 * Scope-picker popup voor het focus-model (F7).
 *
 * Toont een lijst van panelen + canvas; gebruiker kiest één met
 * Arrow↑↓ + Enter of door het cijfer (1-8) te typen. Daarna ontvangt
 * die scope alle Tab/Arrow-navigatie tot een muisklik elders, een
 * nieuwe F7, of Escape.
 *
 * Geen native `<dialog>` of focus-trap-library — een kleine fixed
 * overlay met een keydown-handler in capture-fase doet 't zelf.
 */
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { type FocusScope, SCOPE_LABELS, useKeymap } from "../state/keymap";
import { useUi } from "../state/ui";

/** i18n-key per scope (labels in `scopePicker.scopes.*`). */
const SCOPE_LABEL_KEYS: Record<NonNullable<FocusScope>, string> = {
  canvas: "scopePicker.scopes.canvas",
  explorer: "scopePicker.scopes.explorer",
  "diagrams-panel": "scopePicker.scopes.diagramsPanel",
  "type-palette": "scopePicker.scopes.typePalette",
  "relations-panel": "scopePicker.scopes.relationsPanel",
  "text-pane": "scopePicker.scopes.textPane",
  "attribute-panel": "scopePicker.scopes.attributePanel",
  toolbar: "scopePicker.scopes.toolbar",
};

export function ScopePicker() {
  const { t } = useTranslation();
  const open = useKeymap((s) => s.scopePickerOpen);
  const currentScope = useKeymap((s) => s.scope);
  const setScope = useKeymap((s) => s.setScope);
  const close = useKeymap((s) => s.closeScopePicker);

  // Initial highlight = huidige scope, of eerste optie wanneer scope null.
  const initialIndex = (() => {
    if (!currentScope) return 0;
    const idx = SCOPE_LABELS.findIndex((s) => s.scope === currentScope);
    return idx >= 0 ? idx : 0;
  })();
  const [highlighted, setHighlighted] = useState(initialIndex);
  const listRef = useRef<HTMLUListElement>(null);

  useEffect(() => {
    if (open) setHighlighted(initialIndex);
    // initialIndex is een derived value uit currentScope; we recomputen
    // hem alleen bij open-overgang.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Open een paneel als 't gesloten is — gebruiker kan dan F7 → Explorer
  // doen ook als Explorer net niet geopend was. Mapping per scope-naam.
  const openPanelIfClosed = (scope: NonNullable<FocusScope>) => {
    const ui = useUi.getState();
    switch (scope) {
      case "explorer":
        if (!ui.explorerOpen) ui.setExplorerOpen(true);
        return;
      case "diagrams-panel":
        if (!ui.diagramsPanelOpen) ui.setDiagramsPanelOpen(true);
        return;
      case "type-palette":
        if (!ui.typePaletteOpen) ui.setTypePaletteOpen(true);
        return;
      case "relations-panel":
        if (!ui.relationsPanelOpen) ui.setRelationsPanelOpen(true);
        return;
      case "text-pane":
        if (!ui.textPaneOpen) ui.setTextPaneOpen(true);
        return;
      case "attribute-panel":
        // Attribute-panel rendert alleen wanneer er een selectie is —
        // zonder selectie heeft "open" geen visueel effect maar de
        // flag-set is goedkoop en wordt opgepikt zodra wel een
        // selectie ontstaat.
        if (!ui.attributePanelOpen) ui.setAttributePanelOpen(true);
        return;
      // toolbar / canvas: niet openbaar via flag — toolbar is onderdeel
      // van het canvas en altijd zichtbaar; canvas is altijd zichtbaar.
      default:
        return;
    }
  };

  // Bij scope-wissel naar een panel: open 't paneel als 't dicht is,
  // wacht tot de DOM is ge-update en focus het eerste tabbable element.
  // Canvas-scope gebruikt cursor-cel i.p.v. DOM-focus, dus daar geen
  // focus-shift.
  const commitChoice = (scope: typeof currentScope) => {
    if (!scope) return;
    if (scope !== "canvas") openPanelIfClosed(scope);
    setScope(scope);
    close();
    if (scope === "canvas") return;
    // setTimeout zodat de modal-DOM eerst opgeruimd is, het paneel
    // ge-renderd (na open-flag-change), en mount-animaties klaar zijn.
    setTimeout(() => {
      const scopeEl = document.querySelector<HTMLElement>(`[data-scope="${scope}"]`);
      const first = scopeEl?.querySelector<HTMLElement>(
        'button:not([disabled]), [href], input:not([type="hidden"]):not([disabled]), select:not([disabled]), textarea:not([disabled]), summary, [tabindex]:not([tabindex="-1"])',
      );
      first?.focus();
    }, 0);
  };

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      // Capture-fase + stopPropagation: deze popup eet alle keys zolang
      // 'ie open is, zodat de rest van de app niet per ongeluk z'n eigen
      // shortcuts triggert op een digit-keypress.
      if (e.key === "Escape") {
        e.preventDefault();
        e.stopPropagation();
        close();
        return;
      }
      if (e.key === "Enter") {
        e.preventDefault();
        e.stopPropagation();
        const choice = SCOPE_LABELS[highlighted];
        if (choice) {
          commitChoice(choice.scope);
        }
        return;
      }
      if (e.key === "ArrowDown") {
        e.preventDefault();
        e.stopPropagation();
        setHighlighted((i) => (i + 1) % SCOPE_LABELS.length);
        return;
      }
      if (e.key === "ArrowUp") {
        e.preventDefault();
        e.stopPropagation();
        setHighlighted((i) => (i - 1 + SCOPE_LABELS.length) % SCOPE_LABELS.length);
        return;
      }
      // Digit-shortcuts: 1..8 selecteren direct.
      const digit = Number.parseInt(e.key, 10);
      if (!Number.isNaN(digit) && digit >= 1 && digit <= SCOPE_LABELS.length) {
        e.preventDefault();
        e.stopPropagation();
        const choice = SCOPE_LABELS[digit - 1];
        if (choice) {
          commitChoice(choice.scope);
        }
        return;
      }
    };
    window.addEventListener("keydown", onKey, { capture: true });
    return () => window.removeEventListener("keydown", onKey, { capture: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, highlighted, setScope, close]);

  if (!open) return null;

  return (
    <div
      className="ledger fixed inset-0 z-50 flex items-center justify-center bg-black/30"
      onClick={(e) => {
        if (e.target === e.currentTarget) close();
      }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="scope-picker-title"
    >
      <div className="w-72 rounded-lg border border-(--ld-control) bg-(--ld-surface) shadow-2xl">
        <header className="border-(--ld-border) border-b px-4 py-2">
          <h2 id="scope-picker-title" className="font-semibold text-(--ld-text) text-sm">
            {t("scopePicker.title")}
          </h2>
          <p className="mt-0.5 text-xs text-(--ld-muted)">{t("scopePicker.hint")}</p>
        </header>
        <ul ref={listRef} className="py-1">
          {SCOPE_LABELS.map((option, idx) => {
            const isHighlighted = idx === highlighted;
            const isCurrent = option.scope === currentScope;
            return (
              <li key={option.scope}>
                <button
                  type="button"
                  className={`flex w-full items-center justify-between px-3 py-1.5 text-left text-sm ${
                    isHighlighted
                      ? "bg-(--ld-active) text-(--ld-accent)"
                      : "text-(--ld-text) hover:bg-(--ld-raised)"
                  }`}
                  onClick={() => commitChoice(option.scope)}
                  onMouseEnter={() => setHighlighted(idx)}
                  aria-current={isCurrent ? "true" : undefined}
                >
                  <span>
                    <span className="mr-2 inline-block w-4 text-(--ld-muted) text-xs">
                      {idx + 1}.
                    </span>
                    {t(SCOPE_LABEL_KEYS[option.scope])}
                  </span>
                  {isCurrent && (
                    <span className="text-xs text-(--ld-muted)">{t("scopePicker.current")}</span>
                  )}
                </button>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}
